import * as graphics from "@client/app/graphics";
import {Icon} from "@client/components/Icon";
import {WORKOUT_CARD} from "@client/components/WorkoutBar";
import {Icon as IconType} from "@client/constants";
import * as dialogs from "@client/features/dialogs";
import * as forms from "@client/forms";
import * as widgets from "@client/forms/widgets";
import type {Locator} from "@client/gotterdammerung";
import {Shared, settled, usePendingTransition} from "@client/gotterdammerung";
import {useDebounce} from "@client/hooks";
import * as router from "@client/router";
import type {Props as JournalProps} from "@client/scenes/Journal";
import * as analytics from "@client/shared/analytics";
import * as typography from "@client/shared/typography";
import {useMessages, useSiteContext} from "@client/site/context";
import {convertSecondsToHoursMinutes} from "@client/utils";
import {CheckIcon, MinusIcon, PlusIcon} from "@heroicons/react/20/solid";
import {classNames, reverse, server} from "@reactivated";
import {produce} from "immer";
import {motion} from "motion/react";
import React from "react";

type Assignment = ReturnType<typeof usePlanner>["assignments"][number];

const TARGET_INPUT =
    "w-full rounded-md border-0 px-3 py-1.5 text-gray-900 ring-1 ring-stone-300 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-indigo-600 focus:ring-inset sm:text-sm";

export function TitleAndDescription(props: {
    title: React.ReactNode;
    description: React.ReactNode;
    children?: React.ReactNode;
    actions?: React.ReactNode;
}) {
    return (
        <div className="flex min-h-[35px] flex-1 flex-col justify-center truncate">
            <span className="text-sm font-medium text-gray-950 hover:text-gray-600">
                {props.title}
            </span>
            <div className="-mt-0.5 truncate text-sm font-normal text-gray-500">
                {props.description}
            </div>
            {props.actions != null && (
                <div className="mt-1.5 flex gap-2 text-sm font-light text-gray-900">
                    {props.actions}
                </div>
            )}
            {props.children}
        </div>
    );
}

// type HabitType = keyof Assignment["habits"];
export type HabitType = keyof Assignment["habits"];

type HabitDefinition = {
    name: string;
    icon: IconType;
    colors: string;
    Setup?: (props: {assignment: Assignment}) => React.ReactNode;
    actions?: (props: JournalProps) => React.ReactNode;
};

type Habits = Record<HabitType, HabitDefinition | null>;

export function JournalHabitShell(props: {
    label: string;
    habit: {
        name: string;
        colors: string;
        icon: {node: React.ReactNode} | IconType;
        iconClass?: string;
    };
    content: React.ReactNode;
    onClick?: () => unknown;
    isComplete: boolean | null;
}) {
    const {habit} = props;
    const Element = props.onClick != null ? "button" : "article";
    const isWorkout = props.label == "Workout";
    // Arriving on Complete, the card's check stays gray until the card has
    // landed, then pops. A card below the fold is scrolled to after the
    // transition, never during it.
    const arriving = usePendingTransition() == "complete" && isWorkout;
    const [landing, setLanding] = React.useState(false);
    if (arriving && !landing) {
        setLanding(true);
    }
    const ref = React.useRef<HTMLButtonElement>(null);
    React.useEffect(() => {
        if (!arriving) {
            return;
        }
        void settled().then(() => {
            const box = ref.current?.getBoundingClientRect();
            if (box != null && (box.top < 0 || box.bottom > window.innerHeight)) {
                ref.current?.scrollIntoView({block: "center", behavior: "smooth"});
            }
        });
    }, [arriving]);

    const card = (
        <Element
            ref={ref}
            onClick={props.onClick}
            className={classNames(
                "flex rounded-xl text-left py-2.5 items-center bg-white w-full pr-2",
            )}
            aria-label={props.label}
            data-habit={props.label}
        >
            <div
                className={classNames(
                    "flex shrink-0 items-center justify-center rounded-l-md text-sm font-medium px-2",
                )}
            >
                <div
                    className={classNames(
                        "size-8 rounded-full flex items-center justify-center",
                        habit.colors,
                    )}
                >
                    {typeof habit.icon == "string" ? (
                        <Icon
                            icon={habit.icon}
                            className={habit.iconClass ?? "size-4!"}
                        />
                    ) : (
                        habit.icon.node
                    )}
                </div>
            </div>
            {props.content}
            {props.isComplete != null && (
                <div className="pl-2" data-habit-check>
                    {props.isComplete == true && !arriving && (
                        <motion.div
                            className="mx-auto flex size-7 items-center justify-center rounded-full bg-green-50"
                            initial={
                                landing
                                    ? {backgroundColor: "var(--color-stone-100)"}
                                    : false
                            }
                            animate={{backgroundColor: "var(--color-green-50)"}}
                            transition={{duration: 0.2}}
                        >
                            <motion.span
                                initial={landing ? {opacity: 0, scale: 0.3} : false}
                                animate={{opacity: 1, scale: 1}}
                                transition={{
                                    type: "spring",
                                    stiffness: 500,
                                    damping: 18,
                                }}
                            >
                                <CheckIcon
                                    className="size-4 text-green-600"
                                    aria-hidden="true"
                                />
                            </motion.span>
                        </motion.div>
                    )}
                    {(props.isComplete == false || arriving) && (
                        <div className="mx-auto flex size-7 items-center justify-center rounded-full bg-stone-100"></div>
                    )}
                </div>
            )}
            {[].length == 1 && (
                <div
                    className={classNames(
                        "w-2 flex flex-col items-center justify-center h-4 opacity-70",
                    )}
                >
                    {[].length == 1 && props.onClick != null && (
                        <graphics.ElGrecoCustomChevronRight />
                    )}
                </div>
            )}
        </Element>
    );
    return isWorkout ? <Shared {...WORKOUT_CARD}>{card}</Shared> : card;
}

export function JournalHabit(props: {
    habit: HabitDefinition | {name: string; colors: string; icon: IconType};
    isComplete: boolean | null;
    title?: string;
    content: React.ReactNode;
    handler:
        | {
              onClick: () => unknown;
          }
        | {
              increment: () => void;
              decrement: () => void;
          }
        | {locator: Locator};
}) {
    const {habit, handler} = props;

    const onClick =
        "locator" in handler
            ? () => {
                  void router.transition(handler.locator);
              }
            : "onClick" in handler
              ? handler.onClick
              : undefined;

    // Create content that includes stepper controls for increment/decrement handlers
    const content = (
        <div className="flex flex-1 items-center justify-between truncate">
            <div className="flex-1 truncate pr-2 text-sm">{props.content}</div>
            <div className="shrink-0">
                <div className="flex items-center gap-2">
                    {!("locator" in props.handler) && !("onClick" in props.handler) && (
                        <div className="flex gap-2">
                            <button
                                onClick={props.handler.decrement}
                                type="button"
                                className="rounded-full bg-gray-50 p-1 text-xs font-semibold text-gray-900 shadow-xs hover:bg-gray-100"
                            >
                                <MinusIcon
                                    className="size-5 sm:size-5"
                                    aria-hidden="true"
                                />
                            </button>
                            <button
                                onClick={props.handler.increment}
                                type="button"
                                className="rounded-full bg-gray-50 p-1 text-xs font-semibold text-gray-900 shadow-xs hover:bg-gray-100"
                            >
                                <PlusIcon
                                    className="size-5 sm:size-5"
                                    aria-hidden="true"
                                />
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    return (
        <JournalHabitShell
            label={props.title ?? habit.name}
            habit={{
                name: habit.name,
                colors: habit.colors,
                icon: habit.icon,
            }}
            content={content}
            onClick={onClick}
            isComplete={props.isComplete}
        />
    );
}

export const habits = {
    MACROS: {
        name: "Macros",
        icon: "bullseye",
        colors: "text-indigo-900 bg-indigo-100",
    },
    STEPS: {
        name: "Steps",
        icon: "0302-walk",
        colors: "text-orange-900 bg-orange-100",
    },
    SLEEP: {
        name: "Sleep",
        icon: "0009-bed",
        colors: "text-indigo-900 bg-indigo-100",
    },
    CHECK_IN: {
        name: "Check in",
        icon: "0205-clipboard-check",
        colors: "text-rose-700 bg-rose-100",
    },
    WATER: {
        name: "Drink water",
        icon: "0529-glass2",
        colors: "text-sky-900 bg-sky-100",
    },
    GRATITUDE: {
        name: "Gratitude",
        icon: "0512-gift",
        colors: "text-blue-900 bg-blue-100",
    },
    MEALS: {
        name: "Log meals",
        icon: "0531-dinner2",
        colors: "text-orange-900 bg-orange-100",
    },
    WORKOUT: {
        name: "Workout",
        icon: "0611-bench-press",
        colors: "text-green-900 bg-green-100",
    },
    WEIGHT: {
        name: "Log weight",
        icon: "tachometer",
        colors: "text-purple-900 bg-purple-100",
    },
} satisfies Habits;

function usePlanner(props: {planner: server.journal.day.Planner.output}) {
    const [planner, setPlanner] = React.useState(props.planner);
    const initial = React.useRef(true);

    useDebounce(
        () => {
            if (initial.current == true) {
                initial.current = false;
                return;
            }

            void server.journal.day.planner({
                ...planner,
                assignments: planner.assignments.map((a) => ({
                    ...a,
                    entries: [],
                })),
            });
        },
        500,
        [planner],
    );

    return React.useMemo(
        () => ({
            assignments: planner.assignments.map((assignment, assignmentIndex) => ({
                ...assignment,
                __update: (partialUpdate: Partial<typeof assignment>) => {
                    setPlanner((planner) =>
                        produce(planner, (draft) => {
                            draft.assignments[assignmentIndex] = {
                                ...draft.assignments[assignmentIndex],
                                ...partialUpdate,
                            };
                        }),
                    );
                },
            })),
        }),
        [planner],
    );
}

export function ClientHabit({
    type,
    targets,
    planner,
    days,
    assignment,
    definition,
    client_manager,
    can_schedule_check_in,
}: {
    client_manager: server.manager.templates.ClientManagerPick.output;
    targets: server.journal.nutrition.Target.output[];
    definition: HabitDefinition;
    days: string[];
    assignment: Assignment;
    type: HabitType;
    planner: ReturnType<typeof usePlanner>;
    can_schedule_check_in: boolean;
}) {
    const numberFormatter = new Intl.NumberFormat("en-US");
    const messages = useMessages();
    const disabled = type == "WEIGHT" || type == "MEALS" || type == "WORKOUT";

    const redirectToTarget = () => {
        window.location.href = reverse("client_planner_targets_create", {
            client_manager_id: client_manager.id,
        });
    };

    const macrosDialog = dialogs.useDialog<{
        target: server.journal.nutrition.Target.output | null;
    }>({
        Content: ({close}) => {
            React.useEffect(() => {
                if (targets.length == 0) {
                    redirectToTarget();
                }
            }, []);

            if (targets.length == 0) {
                return null;
            }

            return (
                <div className="flex flex-col gap-4 p-4">
                    <h1 className="text-xl font-semibold">Choose a macro target</h1>
                    <div className="flex flex-col divide-y">
                        {targets.map((target) => (
                            <button
                                key={target.id}
                                type="button"
                                className="py-2"
                                onClick={() => close({target})}
                            >
                                {target.name}
                            </button>
                        ))}
                    </div>
                    <button
                        type="button"
                        className="mx-auto"
                        onClick={redirectToTarget}
                    >
                        <Icon icon="custom-thick-plus">New macro target</Icon>
                    </button>
                    {assignment.target != null && (
                        <button
                            type="button"
                            className="mx-auto"
                            onClick={() => close({target: null})}
                        >
                            <Icon icon="remove">Remove macros</Icon>
                        </button>
                    )}
                </div>
            );
        },
    });

    const gratitudeDialog = dialogs.useDialog<{
        prompt: string | null;
        everyDay: boolean;
    }>({
        Content: ({close}) => {
            const prompt = forms.useField(
                {kind: "textarea", name: ""},
                {
                    initial:
                        assignment.habits.GRATITUDE?.prompt ??
                        "Write down 3 things you're grateful for.",
                },
            );
            const [everyDay, setEveryDay] = React.useState<boolean>(false);

            const field = (
                <forms.Label
                    error={null}
                    label="Customize the prompt below. Leave blank to disable."
                >
                    <forms.Widget field={prompt} />
                </forms.Label>
            );

            return (
                <form
                    className="flex min-w-[320px] flex-col gap-4 p-4 *:flex *:flex-col"
                    onSubmit={(event) => {
                        event.preventDefault();
                        close({
                            prompt: prompt.value == "" ? null : prompt.value,
                            everyDay,
                        });
                    }}
                >
                    {field}
                    <label>
                        Every day of the week
                        <input
                            type="checkbox"
                            checked={everyDay}
                            onChange={(event) => setEveryDay(event.target.checked)}
                        />
                    </label>
                    <typography.Button submit>Submit</typography.Button>
                </form>
            );
        },
    });

    const WATER_UNITS = ["cups", "oz", "liters", "gallons"] as const;
    type WaterUnit = (typeof WATER_UNITS)[number];

    const waterTargetDialog = dialogs.useDialog<{
        target: number | null;
        unit: WaterUnit;
        everyDay: boolean;
    }>({
        Content: ({close}) => {
            const form = forms.useForm(
                {
                    fields: {
                        target: {kind: "number", name: "", nullable: true},
                        unit: {
                            kind: "select",
                            widget: "select",
                            options: WATER_UNITS.map((unit): [WaterUnit, string] => [
                                unit,
                                unit,
                            ]),
                        },
                    },
                },
                {
                    initial: {
                        target: assignment.habits.WATER?.target ?? null,
                        unit: assignment.habits.WATER?.unit ?? "cups",
                    },
                },
            );

            const [everyDay, setEveryDay] = React.useState<boolean>(false);

            return (
                <form
                    className="flex min-w-[320px] flex-col gap-4 p-4 *:flex *:flex-col"
                    onSubmit={(event) => {
                        event.preventDefault();
                        const validated = form.validated;
                        if (!validated.success) {
                            form.setServerErrors(validated.errors);
                            return;
                        }
                        close({...validated.data, everyDay});
                    }}
                >
                    <div className="flex flex-col gap-2">
                        <forms.Label
                            error={form.fields.target.errors?.[0] ?? null}
                            label={`Target`}
                        >
                            <widgets.NumberInput
                                {...form.fields.target}
                                integerOnly
                                className={TARGET_INPUT}
                                placeholder="None"
                            />
                        </forms.Label>
                        <forms.Label error={null} label="Unit">
                            <forms.Widget field={form.fields.unit} />
                        </forms.Label>
                    </div>
                    <label>
                        Every day of the week
                        <input
                            type="checkbox"
                            checked={everyDay}
                            onChange={(event) => setEveryDay(event.target.checked)}
                        />
                    </label>
                    <typography.Button submit>Submit</typography.Button>
                </form>
            );
        },
    });

    const targetValueDialog = dialogs.useDialog<{
        target: number | null;
        everyDay: boolean;
    }>({
        Content: ({close}) => {
            const sleep = forms.useField({kind: "duration", name: "", nullable: true});
            const steps = forms.useField({kind: "number", name: "", nullable: true});
            const target = type == "SLEEP" ? sleep : steps;
            const [everyDay, setEveryDay] = React.useState<boolean>(false);

            const field =
                type == "SLEEP" ? (
                    <forms.Label
                        error={sleep.errors?.[0] ?? null}
                        label="Target sleep time"
                    >
                        <widgets.DurationInput
                            {...sleep}
                            format="hh:mm"
                            className={forms.getFieldClass({})}
                            suffixClassName={forms.getSuffixClass({})}
                        />
                    </forms.Label>
                ) : (
                    // STEPS
                    <forms.Label
                        error={steps.errors?.[0] ?? null}
                        label={"Target steps"}
                    >
                        <widgets.NumberInput
                            {...steps}
                            integerOnly
                            className={TARGET_INPUT}
                            placeholder="None"
                        />
                    </forms.Label>
                );

            return (
                <form
                    className="flex min-w-[320px] flex-col gap-4 p-4 *:flex *:flex-col"
                    onSubmit={(event) => {
                        event.preventDefault();
                        const validated = target.validated;
                        if (!validated.success) {
                            target.setServerErrors(validated.errors);
                            return;
                        }
                        close({target: validated.data, everyDay});
                    }}
                >
                    {field}
                    <label>
                        Every day of the week
                        <input
                            type="checkbox"
                            checked={everyDay}
                            onChange={(event) => setEveryDay(event.target.checked)}
                        />
                    </label>
                    <typography.Button submit>Submit</typography.Button>
                </form>
            );
        },
    });

    return (
        <>
            {macrosDialog.dialog}
            {gratitudeDialog.dialog}
            {targetValueDialog.dialog}
            {waterTargetDialog.dialog}
            <button
                key={type}
                type="button"
                className={classNames(
                    definition.colors,
                    "flex items-center gap-1.5 text-sm px-2 py-1.5 rounded-md",
                    disabled && "opacity-70",
                )}
                onClick={async () => {
                    if (disabled) {
                        void analytics.track("SELECTED_DISABLED_HABIT", {
                            habit_type: type,
                        });
                        messages.info(
                            `The **${definition.name}** habit is coming soon!`,
                        );
                        return;
                    }

                    void analytics.track("SELECTED_HABIT", {
                        habit_type: type,
                    });

                    if (type == "CHECK_IN") {
                        if (can_schedule_check_in == false) {
                            messages.info(
                                "To enable check-ins you must first choose your check-in questions. You can find these on the Dashboard under Settings",
                            );
                            return;
                        }
                        assignment.__update({
                            check_in:
                                assignment.check_in == "ENABLED"
                                    ? "DISABLED"
                                    : "ENABLED",
                        });
                        return;
                    }

                    if (type == "MACROS") {
                        const result = await macrosDialog();

                        if (result != null) {
                            assignment.__update({target: result.target});
                        }
                        return;
                    }
                    if (type == "GRATITUDE") {
                        const result = await gratitudeDialog();

                        if (result != null) {
                            if (result.everyDay == true) {
                                days.forEach((_, index) =>
                                    planner.assignments[index].__update({
                                        habits: {
                                            ...planner.assignments[index].habits,
                                            [type]:
                                                result.prompt == null
                                                    ? undefined
                                                    : {
                                                          prompt: result.prompt,
                                                      },
                                        },
                                    }),
                                );
                            } else {
                                assignment.__update({
                                    habits: {
                                        ...assignment.habits,
                                        [type]:
                                            result.prompt == null
                                                ? undefined
                                                : {
                                                      prompt: result.prompt,
                                                  },
                                    },
                                });
                            }
                        }
                        return;
                    }

                    if (type === "WATER") {
                        const result = await waterTargetDialog();
                        if (result != null) {
                            if (result.everyDay == true) {
                                days.forEach((_, index) =>
                                    planner.assignments[index].__update({
                                        habits: {
                                            ...planner.assignments[index].habits,
                                            WATER:
                                                result.target === null
                                                    ? undefined
                                                    : {
                                                          target: result.target,
                                                          unit: result.unit,
                                                      },
                                        },
                                    }),
                                );
                            } else {
                                assignment.__update({
                                    habits: {
                                        ...assignment.habits,
                                        WATER:
                                            result.target === null
                                                ? undefined
                                                : {
                                                      target: result.target,
                                                      unit: result.unit,
                                                  },
                                    },
                                });
                            }
                        }
                    } else {
                        // SLEEP or STEPS
                        const result = await targetValueDialog();
                        if (result != null) {
                            if (result.everyDay == true) {
                                days.forEach((_, index) =>
                                    planner.assignments[index].__update({
                                        habits: {
                                            ...planner.assignments[index].habits,
                                            [type]:
                                                result.target === null
                                                    ? undefined
                                                    : result.target,
                                        },
                                    }),
                                );
                            } else {
                                assignment.__update({
                                    habits: {
                                        ...assignment.habits,
                                        [type]:
                                            result.target === null
                                                ? undefined
                                                : result.target,
                                    },
                                });
                            }
                        }
                    }
                }}
            >
                <Icon icon={definition.icon} large="relative" />
                <div className={classNames("flex flex-col text-left overflow-hidden")}>
                    <div className="truncate font-medium">{definition.name}</div>
                    <div className="text-xs">
                        {assignment.habits[type] == null &&
                            ["SLEEP", "STEPS", "WATER"].includes(type) && (
                                <div>No target</div>
                            )}
                        {assignment.habits[type] != null && (
                            <>
                                {type == "SLEEP" && (
                                    <div>
                                        {convertSecondsToHoursMinutes(
                                            assignment.habits[type],
                                        )}
                                    </div>
                                )}
                                {type == "WATER" && (
                                    <div>
                                        {assignment.habits[type].target}{" "}
                                        {assignment.habits[type].unit ?? "cups"}
                                    </div>
                                )}
                                {type == "STEPS" && (
                                    <div>
                                        {numberFormatter.format(
                                            assignment.habits[type],
                                        )}{" "}
                                        steps
                                    </div>
                                )}
                            </>
                        )}

                        {type == "GRATITUDE" && (
                            <div className="truncate">
                                {assignment.habits[type] != null
                                    ? "Enabled"
                                    : "Disabled"}
                            </div>
                        )}

                        {type == "CHECK_IN" && (
                            <div className="truncate">
                                {assignment.check_in == "ENABLED"
                                    ? "Enabled"
                                    : "Disabled"}
                            </div>
                        )}
                        {type == "MACROS" && assignment.target == null && (
                            <div className="truncate">No macros</div>
                        )}
                        {type == "MACROS" && assignment.target != null && (
                            <div className="truncate">{assignment.target.name}</div>
                        )}
                    </div>
                </div>
            </button>
        </>
    );
}

export function ClientHabits(props: {
    client_manager: server.manager.templates.ClientManagerPick.output;
    planner: server.journal.day.Planner.output;
    can_schedule_check_in: boolean;
    targets: server.journal.nutrition.Target.output[];
}) {
    const days = [
        "Monday",
        "Tuesday",
        "Wed",
        "Thursday",
        "Friday",
        "Saturday",
        "Sunday",
    ];
    const planner = usePlanner(props);
    const {profile} = useSiteContext();

    return (
        <div className="overflow-x-auto sm:-mx-4">
            <div className="grid min-h-120 min-w-[1100px] grid-cols-7 gap-1">
                {days.map((day, day_of_week) => {
                    return (
                        <div
                            key={day}
                            className="rounded-md border border-gray-200 bg-white"
                        >
                            <div className="border-b border-gray-200 py-2 text-center text-sm font-semibold">
                                {day}
                            </div>
                            <div className="flex flex-col gap-2 p-1">
                                {Object.entries(habits)
                                    .map(
                                        ([type, definition]) =>
                                            [type as HabitType, definition] as const,
                                    )
                                    .map(([type, definition]) => (
                                        <ClientHabit
                                            key={type}
                                            targets={props.targets}
                                            can_schedule_check_in={
                                                props.can_schedule_check_in
                                            }
                                            client_manager={props.client_manager}
                                            type={type}
                                            assignment={
                                                planner.assignments[day_of_week]
                                            }
                                            days={days}
                                            planner={planner}
                                            definition={definition}
                                        />
                                    ))}
                                {profile?.user.has_beta_access == true && (
                                    <a
                                        className={classNames(
                                            "text-sky-900 bg-sky-100",
                                            "flex items-center gap-1.5 text-sm px-2 py-1.5 rounded-md",
                                        )}
                                        href={reverse("client_supplements", {
                                            client_manager_id: props.client_manager.id,
                                        })}
                                    >
                                        <Icon icon="0492-pills" large="relative" />
                                        <div className="flex flex-col overflow-hidden text-left">
                                            <div className="font-medium whitespace-nowrap">
                                                Supplements
                                            </div>
                                            <div className="text-xs">
                                                {
                                                    planner.assignments[day_of_week]
                                                        .supplements.length
                                                }{" "}
                                                required
                                            </div>
                                        </div>
                                    </a>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
